import React, {useCallback, useEffect, useRef, useState} from 'react';
import Aws from '@/services/aws/aws';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {
  ACCEPTED_MESSAGE_FILE_TYPES,
  MAX_MESSAGE_ATTACHMENT_SIZE_BYTES,
} from '@/config/conversationAttachments';
import type {ConversationMessageAttachment} from '@/types/conversationMessage';
import conversationMessagesUpdaters from '@/api/updaters/conversationMessages';

export type PendingAttachment = {
  sourceId: string;
  fileName: string;
  mimeType: string;
};

function useMessageAttachments(): {
  pendingAttachments: PendingAttachment[];
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleAttachClick: () => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveAttachment: (sourceId: string) => void;
  sendAttachments: (
    messageId: string
  ) => Promise<ConversationMessageAttachment[]>;
  reset: () => void;
} {
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const awsRef = useRef<Aws | null>(null);

  useEffect(() => {
    return () => {
      awsRef.current?.dispose();
    };
  }, []);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selected = Array.from(event.target.files ?? []);
    const oversized = selected.filter(
      (f) => f.size > MAX_MESSAGE_ATTACHMENT_SIZE_BYTES
    );
    if (oversized.length > 0) {
      raiseFlashMessageError({
        title: translate('File too large'),
        subtitle: translate('Attachments must be under 100 MB'),
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    const files = selected.filter((f) =>
      ACCEPTED_MESSAGE_FILE_TYPES.includes(f.type)
    );
    if (!files.length) return;

    const awsService = awsRef.current ?? (awsRef.current = new Aws());
    const bucket = Aws.s3Settings.buckets.messageAttachments;
    if (!bucket) return;

    try {
      setIsUploading(true);
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const s3Url = await awsService.uploadToBucket(bucket, {
            name: file.name,
            size: file.size,
            type: file.type,
            blob: file,
          });
          return {
            sourceId: s3Url.split('.com')[1],
            fileName: file.name,
            mimeType: file.type,
          };
        })
      );
      setPendingAttachments((prev) => [...prev, ...uploaded]);
    } catch {
      raiseFlashMessageError({
        title: translate('Failed to upload attachment'),
        subtitle: translate('Please check your connection and try again'),
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (sourceId: string) => {
    setPendingAttachments((prev) =>
      prev.filter((a) => a.sourceId !== sourceId)
    );
  };

  // Memoized so consumers can safely list it in effect/callback deps without
  // triggering re-runs on every render. `setPendingAttachments` is stable, so
  // an empty dep array is correct.
  const reset = useCallback(() => {
    setPendingAttachments([]);
  }, []);

  const sendAttachments = useCallback(
    async (messageId: string): Promise<ConversationMessageAttachment[]> => {
      if (pendingAttachments.length === 0) {
        return [];
      }

      try {
        return await Promise.all(
          pendingAttachments.map((a) =>
            conversationMessagesUpdaters.createAttachment(messageId, a)
          )
        );
      } catch {
        raiseFlashMessageError({
          title: translate('Failed to send attachment'),
          subtitle: translate('Please try again'),
        });
        return [];
      } finally {
        reset();
      }
    },
    [pendingAttachments, reset]
  );

  return {
    pendingAttachments,
    isUploading,
    fileInputRef,
    handleAttachClick,
    handleFileChange,
    handleRemoveAttachment,
    sendAttachments,
    reset,
  };
}

export default useMessageAttachments;
